import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Sprout, Upload, FileText, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import Navigation from '../components/Navigation';
import { getAdminHeaders, canEdit } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LancamentoDiario = () => {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [anexoFilename, setAnexoFilename] = useState('');
  const [settings, setSettings] = useState({ devocional: 0 });
  const [formData, setFormData] = useState({
    data: (() => { const n = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' })); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })(),
    child_id: '',
    devocional: false,
    evidencia: '',
    anexo_url: ''
  });

  useEffect(() => {
    fetchChildren();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const fetchChildren = async () => {
    try {
      const res = await axios.get(`${API}/children`);
      setChildren(res.data);
    } catch (error) {
      console.error('Erro ao carregar crianças:', error);
    }
  };

  const calculatePoints = () => {
    let points = 0;
    if (formData.devocional) points += settings.devocional;
    return points;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.child_id) {
      toast.error('Selecione uma criança');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/activities/daily`, formData, { headers: getAdminHeaders() });
      toast.success('Atividade diária registrada com sucesso!');
      
      setFormData({
        data: (() => { const n = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' })); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}-${String(n.getDate()).padStart(2,'0')}`; })(),
        child_id: '',
        devocional: false,
        versiculo: false,
        evidencia: '',
        anexo_url: ''
      });
      setAnexoFilename('');
    } catch (error) {
      console.error('Erro ao registrar:', error);
      const msg = error.response?.data?.detail || 'Erro ao registrar atividade';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7] pb-24">
      <div className="bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-7 h-7" strokeWidth={2.5} />
              <h1 className="font-nunito font-bold text-3xl">Lançamento Diário</h1>
            </div>
            <img src="/logos/logo_semente.png" alt="Projeto Semente" className="w-36 h-36 drop-shadow-lg" />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <Card className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#E1ECE5]">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Data */}
            <div>
              <Label htmlFor="data" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Data
              </Label>
              <Input
                id="data"
                type="date"
                data-testid="input-data"
                value={formData.data}
                onChange={(e) => setFormData(prev => ({ ...prev, data: e.target.value }))}
                className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50"
                required
              />
            </div>

            {/* Criança */}
            <div>
              <Label htmlFor="child" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Criança *
              </Label>
              <Select
                value={formData.child_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, child_id: value }))}
              >
                <SelectTrigger
                  data-testid="select-child"
                  className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] bg-gray-50/50"
                >
                  <SelectValue placeholder="Selecione uma criança" />
                </SelectTrigger>
                <SelectContent>
                  {children.map((child) => (
                    <SelectItem key={child.id} value={child.id}>
                      {child.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Devocional */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
              <div>
                <Label htmlFor="devocional" className="font-inter font-semibold text-[#1A4D2E] cursor-pointer">
                  Devocional
                </Label>
                <p className="font-inter text-sm text-gray-500">+{settings.devocional} sementes</p>
              </div>
              <Switch
                id="devocional"
                data-testid="switch-devocional"
                checked={formData.devocional}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, devocional: checked }))}
                className="data-[state=checked]:bg-[#1A4D2E]"
              />
            </div>

            {/* Evidência */}
            <div>
              <Label htmlFor="evidencia" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Evidência ou Observação
              </Label>
              <Textarea
                id="evidencia"
                data-testid="input-evidencia"
                value={formData.evidencia}
                onChange={(e) => setFormData(prev => ({ ...prev, evidencia: e.target.value }))}
                className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50 min-h-[80px]"
                placeholder="Anotações sobre a atividade..."
              />
            </div>

            {/* Anexo */}
            <div>
              <Label className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                Anexar Arquivo (Opcional)
              </Label>
              {formData.anexo_url ? (
                <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                  <FileText className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="font-inter text-sm text-green-700 flex-1 truncate">{anexoFilename}</span>
                  <button
                    type="button"
                    data-testid="remove-anexo"
                    onClick={() => { setFormData(prev => ({ ...prev, anexo_url: '' })); setAnexoFilename(''); }}
                    className="text-red-500 hover:bg-red-50 rounded-full p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label
                  data-testid="upload-anexo"
                  className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-xl cursor-pointer transition-all ${uploading ? 'border-gray-300 bg-gray-50' : 'border-[#1A4D2E]/30 hover:border-[#1A4D2E] hover:bg-[#1A4D2E]/5'}`}
                >
                  <Upload className="w-5 h-5 text-[#1A4D2E]" />
                  <span className="font-inter text-sm text-[#1A4D2E]">
                    {uploading ? 'Enviando...' : 'Foto, PDF ou imagem (max 5MB)'}
                  </span>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    className="hidden"
                    disabled={uploading}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) { toast.error('Arquivo muito grande. Maximo 5MB'); return; }
                      setUploading(true);
                      try {
                        const fd = new FormData();
                        fd.append('file', file);
                        const res = await axios.post(`${API}/upload/document`, fd, { headers: { ...getAdminHeaders(), 'Content-Type': 'multipart/form-data' } });
                        setFormData(prev => ({ ...prev, anexo_url: res.data.url }));
                        setAnexoFilename(file.name);
                        toast.success('Arquivo anexado!');
                      } catch (err) { toast.error('Erro ao enviar arquivo'); }
                      finally { setUploading(false); }
                    }}
                  />
                </label>
              )}
            </div>

            {/* Pontos Calculados */}
            <Card className="bg-gradient-to-r from-[#F2F7F4] to-[#FEFAE6] rounded-xl p-6 border border-[#1A4D2E]/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sprout className="w-8 h-8 text-[#1A4D2E]" />
                  <div>
                    <p className="font-inter text-sm text-gray-600">Pontos do Dia</p>
                    <p className="font-nunito font-bold text-3xl text-[#1A4D2E]">
                      {calculatePoints()}
                    </p>
                  </div>
                </div>
                <p className="font-inter text-sm text-gray-500">sementes</p>
              </div>
            </Card>

            {/* Submit Button */}
            <Button
              type="submit"
              data-testid="submit-button"
              disabled={loading}
              className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold shadow-md active:scale-95 transition-all"
            >
              {loading ? 'Salvando...' : 'Salvar Lançamento'}
            </Button>
          </form>
        </Card>
      </div>

      <Navigation active="lancamento" />
    </div>
  );
};

export default LancamentoDiario;
